'use client'

import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react'
import { db, isFirebaseConfigured } from '@/lib/firebase/firebase'
import { onValue, ref, set } from 'firebase/database'
import { signInWithAppToken } from '@/lib/firebase/auth'
import { useUserContext } from '@/lib/contexts/UserContext'

export interface RealtimeUserStatus {
  state: 'online' | 'offline' | null
  download_method: 'portal' | 'browser' | null
  loading: boolean
  error: string | null
  isUpdating: boolean
  setDownloadMethod: (method: 'portal' | 'browser') => Promise<void>
  setState: (state: 'online' | 'offline' | null) => Promise<void>
}

// Create context
const UserRealtimeContext = createContext<RealtimeUserStatus | undefined>(undefined)

const UserRealtimeContextProvider = ({ children }: PropsWithChildren) => {
  const [data, setData] = useState<Omit<RealtimeUserStatus, 'setDownloadMethod' | 'setState'>>({
    state: null,
    download_method: null,
    loading: true,
    error: null,
    isUpdating: false,
  })
  const [uid, setUid] = useState<string | null>(null)

  const { user } = useUserContext()

  const setDownloadMethod = async (method: 'portal' | 'browser') => {
    if (!uid || !isFirebaseConfigured || !db) {
      console.warn('Cannot set download method: Firebase not configured or user not authenticated')
      return
    }

    setData((prev) => ({ ...prev, isUpdating: true }))

    try {
      const downloadMethodRef = ref(db, `users/${uid}/download_method`)
      await set(downloadMethodRef, method)
      console.debug('Download method updated to:', method)
    } catch (err: any) {
      console.error('Failed to update download method:', err)
      setData((prev) => ({ ...prev, error: err.message }))
    } finally {
      setData((prev) => ({ ...prev, isUpdating: false }))
    }
  }

  const setState = async (state: 'online' | 'offline' | null) => {
    if (!uid || !isFirebaseConfigured || !db) {
      console.warn('Cannot set state: Firebase not configured or user not authenticated')
      return
    }

    setData((prev) => ({ ...prev, isUpdating: true }))

    try {
      const stateRef = ref(db, `users/${uid}/state`)
      await set(stateRef, state)
      console.debug('State updated to:', state)
    } catch (err: any) {
      console.error('Failed to update state:', err)
      setData((prev) => ({ ...prev, error: err.message }))
    } finally {
      setData((prev) => ({ ...prev, isUpdating: false }))
    }
  }

  useEffect(() => {
    if (!user) return

    const database = db

    if (!isFirebaseConfigured || !database) {
      console.warn('Firebase is not configured; realtime user state is disabled.')
      setData({ state: null, download_method: null, loading: false, error: null, isUpdating: false })
      return
    }

    let unsubscribe: (() => void) | undefined

    async function init() {
      console.debug('Initializing Firebase user state listener...')

      try {
        setData((prev) => ({ ...prev, loading: true, error: null }))

        const { uid: authenticatedUid } = await signInWithAppToken()
        if (!authenticatedUid) throw new Error('User not authenticated')

        console.debug('Firebase authenticated with UID:', authenticatedUid)
        setUid(authenticatedUid)

        const userRef = ref(database!, `users/${authenticatedUid}`)

        unsubscribe = onValue(userRef, (snap) => {
          const val = snap.val()
          setData((prev) => ({
            ...prev,
            state: val?.state ?? null,
            download_method: val?.download_method ?? null,
            loading: false,
            error: null,
          }))

          console.debug('Received user state update:', {
            state: val?.state ?? null,
            download_method: val?.download_method ?? null,
          })
        })
      } catch (err: any) {
        setData({ state: null, download_method: null, loading: false, error: err.message, isUpdating: false })
      }
    }

    init()

    return () => {
      if (unsubscribe) unsubscribe()
    }
  }, [user])

  const value: RealtimeUserStatus = {
    ...data,
    setDownloadMethod,
    setState,
  }

  return <UserRealtimeContext.Provider value={value}>{children}</UserRealtimeContext.Provider>
}

const useUserRealtimeContext = () => {
  const ctx = useContext(UserRealtimeContext)
  if (ctx === undefined) {
    throw new Error('useUserRealtimeContext must be used within a UserRealtimeContextProvider')
  }
  return ctx
}

export { UserRealtimeContextProvider, useUserRealtimeContext }
